﻿import {
  ArrowLeft,
  ArrowRight,
  Ban,
  Check,
  CheckCircle2,
  Copy,
  CreditCard,
  Download,
  FileText,
  FlaskConical,
  Gift,
  Loader2,
  Mail,
  MessageSquare,
  RefreshCcw,
  RotateCcw,
  Ticket,
  Wallet,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import type {
  InvoiceStatus,
  InvoiceSummary,
  OrderDetailResponse,
  OrderPaymentInfo,
  OrderStatus,
  RefundInfo,
} from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { QueryError } from "@/components/query-error";
import {
  downloadInvoicePdf,
  useCancelOrderMutation,
  useOrderInvoicingQuery,
  useOrderQuery,
  useResendEmailMutation,
  useResendSmsMutation,
} from "@/features/orders/api";
import { RefundDialog } from "@/features/orders/refund-dialog";
import { useMeQuery } from "@/features/organizers/api";
import { ApiError } from "@/lib/api-client";
import { useRefundReasonLabel } from "@/lib/enum-labels";
import { ISTANBUL_TZ, useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

const DETAIL_DATE: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: ISTANBUL_TZ,
};

const STATUS_VARIANTS: Record<
  OrderStatus,
  "success" | "warning" | "secondary" | "destructive" | "outline" | "default"
> = {
  Pending: "warning",
  Paid: "success",
  Cancelled: "destructive",
  Expired: "secondary",
  Refunded: "outline",
  PartiallyRefunded: "warning",
};

export function OrderDetailPage() {
  return (
    <DashboardPage>
      <OrderDetailView />
    </DashboardPage>
  );
}

function OrderDetailView() {
  const params = useParams<{ orderNumber: string }>();
  const orderNumber = params.orderNumber ?? "";
  const { data, isLoading, error, refetch, isRefetching } = useOrderQuery(orderNumber);
  const t = useT("orderDetail");
  const tStatus = useT("orderStatus");

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/orders"
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" />
          {t("back")}
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-2xl font-semibold tracking-tight">
            {orderNumber}
          </h1>
          {data && (
            <Badge variant={STATUS_VARIANTS[data.status]}>
              {tStatus(data.status)}
            </Badge>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <QueryError
          message={t("loadError")}
          onRetry={() => refetch()}
          retrying={isRefetching}
        />
      ) : data ? (
        <OrderDetailContent order={data} />
      ) : null}
    </div>
  );
}

function OrderDetailContent({ order }: { order: OrderDetailResponse }) {
  const t = useT("orderDetail");
  const tTicket = useT("ticketStatus");
  const fmt = useFormat();
  const money = (value: number) => fmt.money(value, order.currency);
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("buyerTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
            <Field label={t("fullName")}>
              {order.buyerFirstName} {order.buyerLastName}
            </Field>
            <Field label={t("email")}>
              <span dir="ltr">{order.buyerEmail}</span>
            </Field>
            <Field label={t("phone")}>
              <span dir="ltr">{order.buyerPhone || "—"}</span>
            </Field>
            <Field label={t("event")}>
              <div>{order.eventTitle}</div>
              <Link
                to={`/events/${order.eventSlug}`}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
              >
                {t("openEvent")}
                <ArrowRight className="h-3 w-3 rtl:-scale-x-100" />
              </Link>
            </Field>
            <Field label={t("createdAt")}>{fmt.dateTime(order.createdAt, DETAIL_DATE)}</Field>
            {order.paidAt && (
              <Field label={t("paidAt")}>{fmt.dateTime(order.paidAt, DETAIL_DATE)}</Field>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("itemsTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-start font-medium">{t("ticketType")}</th>
                    <th className="px-4 py-2 text-end font-medium">{t("quantity")}</th>
                    <th className="px-4 py-2 text-end font-medium">{t("unit")}</th>
                    <th className="px-4 py-2 text-end font-medium">{t("amount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((it) => (
                    <tr key={it.id} className="border-t">
                      <td className="px-4 py-2">
                        {it.ticketTypeName}
                        {it.isFree && (
                          <Badge variant="secondary" className="ms-2">
                            {t("free")}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-end tabular-nums">
                        {fmt.number(it.quantity)}
                      </td>
                      <td className="px-4 py-2 text-end tabular-nums">
                        {money(it.unitPrice)}
                      </td>
                      <td className="px-4 py-2 text-end tabular-nums font-medium">
                        {money(it.subtotal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {order.tickets.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Ticket className="h-4 w-4" />
                {t("ticketsTitle", { count: order.tickets.length })}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-start font-medium">{t("code")}</th>
                      <th className="px-4 py-2 text-start font-medium">{t("type")}</th>
                      <th className="px-4 py-2 text-start font-medium">{t("status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.tickets.map((tk) => (
                      <tr key={tk.id} className="border-t">
                        <td className="px-4 py-2 font-mono text-xs">
                          {tk.ticketCode}
                        </td>
                        <td className="px-4 py-2">{tk.ticketTypeName}</td>
                        <td className="px-4 py-2">
                          <Badge
                            variant={
                              tk.status === "Valid"
                                ? "success"
                                : tk.status === "Used"
                                ? "secondary"
                                : "destructive"
                            }
                          >
                            {tTicket(tk.status)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {order.lastPaymentError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            <div className="font-medium">{t("lastPaymentError")}</div>
            <div className="mt-1 text-xs">{order.lastPaymentError}</div>
          </div>
        )}

        {order.refunds.length > 0 && <RefundTimelineCard order={order} />}
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("summaryTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Row label={t("subtotal")} value={money(order.subtotal)} />
            {order.discountTotal > 0 && (
              <Row label={t("discount")} value={`−${money(order.discountTotal)}`} />
            )}
            {order.taxTotal > 0 && <Row label={t("tax")} value={money(order.taxTotal)} />}
            {order.serviceFeeTotal > 0 && (
              <Row label={t("serviceFee")} value={money(order.serviceFeeTotal)} />
            )}
            <div className="border-t pt-2" />
            <Row label={t("total")} value={money(order.total)} bold />
          </CardContent>
        </Card>

        {order.payment?.provider && (
          <PaymentCard payment={order.payment} currency={order.currency} />
        )}

        <InvoiceCard order={order} />

        <OrderActions order={order} />
      </div>
    </div>
  );
}

const PAYMENT_PROVIDER_ICONS: Record<string, typeof CreditCard> = {
  Param: CreditCard,
  Isbank: CreditCard,
  Halkbank: CreditCard,
  Tami: CreditCard,
  Garanti: CreditCard,
  Wallet: Wallet,
  Free: Gift,
  Dev: FlaskConical,
};

function PaymentCard({
  payment,
  currency,
}: {
  payment: OrderPaymentInfo;
  currency: string;
}) {
  const t = useT("orderDetail.payment");
  const tCommon = useT("common");
  const fmt = useFormat();
  const [copied, setCopied] = useState(false);
  const provider = payment.provider ?? "";
  const Icon = PAYMENT_PROVIDER_ICONS[provider] ?? CreditCard;
  const label = provider in PAYMENT_PROVIDER_ICONS ? t(`providers.${provider}`) : provider;

  async function copyTransactionId() {
    if (!payment.transactionId) return;
    try {
      await navigator.clipboard.writeText(payment.transactionId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(tCommon("copyFailed"));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4" />
          {t("title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div>{label}</div>
        {payment.transactionId && (
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-xs text-muted-foreground">{t("transactionId")}</div>
              <div className="truncate font-mono text-xs">{payment.transactionId}</div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={copyTransactionId}
              aria-label={t("copyTransactionId")}
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
        )}
        {payment.installmentCount > 1 && (
          <Row
            label={t("installments", { count: payment.installmentCount })}
            value={fmt.money(payment.installmentFeeTotal, currency)}
          />
        )}
      </CardContent>
    </Card>
  );
}

const INVOICE_STATUS_VARIANTS: Record<
  InvoiceStatus,
  "success" | "warning" | "secondary" | "destructive"
> = {
  Pending: "warning",
  ContactCreated: "warning",
  InvoiceCreated: "warning",
  Paid: "warning",
  DocumentQueued: "warning",
  DocumentIssued: "success",
  Completed: "success",
  Skipped: "secondary",
  Failed: "destructive",
};

/**
 * Alıcı faturası etkinlik bittikten sonra kesilir; o zamana kadar sadece
 * checkout'ta girilen fatura bilgileri görünür.
 */
function InvoiceCard({ order }: { order: OrderDetailResponse }) {
  const isBillable = order.status === "Paid" || order.status === "PartiallyRefunded"
    || order.status === "Refunded";
  const { data, isLoading } = useOrderInvoicingQuery(isBillable ? order.orderNumber : "");
  const t = useT("orderDetail.invoice");

  if (!isBillable) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileText className="h-4 w-4" />
          {t("title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {isLoading || !data ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <>
            {data.billing.isCorporate ? (
              <div className="space-y-0.5">
                <div className="font-medium">{data.billing.companyName}</div>
                <div className="text-xs text-muted-foreground">
                  {t("taxLine", {
                    taxNumber: data.billing.taxNumber,
                    taxOffice: data.billing.taxOffice,
                  })}
                </div>
                <div className="text-xs text-muted-foreground">
                  {data.billing.address}, {data.billing.district}/{data.billing.city}
                </div>
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                {t("individual")}
                {data.billing.tcKimlikMasked && (
                  <> · {t("nationalId", { value: data.billing.tcKimlikMasked })}</>
                )}
              </div>
            )}

            {data.invoices.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {data.enabled ? t("pendingAuto") : t("disabled")}
              </p>
            ) : (
              <div className="space-y-2">
                {data.invoices.map((invoice) => (
                  <InvoiceRow
                    key={invoice.id}
                    orderNumber={order.orderNumber}
                    invoice={invoice}
                    currency={order.currency}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function InvoiceRow({
  orderNumber,
  invoice,
  currency,
}: {
  orderNumber: string;
  invoice: InvoiceSummary;
  currency: string;
}) {
  const t = useT("orderDetail.invoice");
  const tStatus = useT("invoiceStatus");
  const fmt = useFormat();
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      await downloadInvoicePdf(orderNumber, invoice.id);
    } catch (e) {
      toast.error(getErrorMessage(e, t("downloadFailed")));
    } finally {
      setDownloading(false);
    }
  }

  const label = invoice.kind === "Refund"
    ? t("kinds.refund")
    : invoice.documentType === "EInvoice" ? t("kinds.eInvoice") : t("kinds.eArchive");

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border p-2.5">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium">{label}</span>
          <Badge variant={INVOICE_STATUS_VARIANTS[invoice.status]}>
            {tStatus(invoice.status)}
          </Badge>
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {fmt.money(invoice.grossTotal, currency)}
          {invoice.completedAt && <> · {fmt.dateTime(invoice.completedAt, DETAIL_DATE)}</>}
        </div>
      </div>
      {invoice.hasPdf && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleDownload}
          disabled={downloading}
          aria-label={t("download")}
          title={t("download")}
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
        </Button>
      )}
    </div>
  );
}

/**
 * Durum-bazlı aksiyon paneli:
 * - Pending → İptal Et (opsiyonel sebep)
 * - Paid / PartiallyRefunded → İade Et (modal: bilet seçim + reason + notif),
 *   Onay e-posta / SMS tekrar gönder
 * - Refunded / Cancelled / Expired → terminal, aksiyon yok
 *
 * İptal ve iade yalnız Owner + Manager (RefundExecute); Editor / Accountant
 * sadece onay tekrar gönderimini görür.
 */
function OrderActions({ order }: { order: OrderDetailResponse }) {
  const t = useT("orderDetail.actions");
  const me = useMeQuery();
  const role = me.data?.role;
  const canRefund = role === "Owner" || role === "Manager";
  const [cancelOpen, setCancelOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [reason, setReason] = useState("");

  const cancelMut = useCancelOrderMutation(order.orderNumber);
  const emailMut = useResendEmailMutation(order.orderNumber);
  const smsMut = useResendSmsMutation(order.orderNumber);

  async function handleCancel() {
    try {
      await cancelMut.mutateAsync({ reason: reason.trim() || undefined });
      toast.success(t("cancelled"));
      setCancelOpen(false);
      setReason("");
    } catch (e) {
      toast.error(getErrorMessage(e, t("cancelFailed")));
    }
  }

  async function handleResendEmail() {
    try {
      await emailMut.mutateAsync();
      toast.success(t("emailSent"));
    } catch (e) {
      toast.error(getErrorMessage(e, t("emailFailed")));
    }
  }

  async function handleResendSms() {
    try {
      await smsMut.mutateAsync();
      toast.success(t("smsSent"));
    } catch (e) {
      toast.error(getErrorMessage(e, t("smsFailed")));
    }
  }

  if (order.status === "Pending") {
    if (!canRefund) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!cancelOpen ? (
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => setCancelOpen(true)}
            >
              <Ban className="h-4 w-4" />
              {t("cancelOrder")}
            </Button>
          ) : (
            <div className="space-y-2">
              <label htmlFor="cancel-reason" className="block text-sm font-medium">
                {t("cancelReason")}
              </label>
              <Textarea
                id="cancel-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder={t("cancelPlaceholder")}
                maxLength={500}
              />
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleCancel}
                  disabled={cancelMut.isPending}
                >
                  {cancelMut.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Ban className="h-4 w-4" />
                  )}
                  {t("cancel")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCancelOpen(false);
                    setReason("");
                  }}
                  disabled={cancelMut.isPending}
                >
                  {t("dismiss")}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (order.status === "Paid" || order.status === "PartiallyRefunded") {
    const hasActiveTickets = order.tickets.some((t) => t.status === "Valid");
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("title")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleResendEmail}
              disabled={emailMut.isPending}
            >
              {emailMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mail className="h-4 w-4" />
              )}
              {t("resendEmail")}
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={handleResendSms}
              disabled={smsMut.isPending || !order.buyerPhone}
              title={!order.buyerPhone ? t("noPhone") : undefined}
            >
              {smsMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MessageSquare className="h-4 w-4" />
              )}
              {t("resendSms")}
            </Button>
            {canRefund && (
              <>
                <div className="pt-1" />
                <Button
                  variant="destructive"
                  className="w-full justify-start"
                  onClick={() => setRefundOpen(true)}
                  disabled={!hasActiveTickets}
                  title={
                    !hasActiveTickets ? t("noActiveTickets") : undefined
                  }
                >
                  <RefreshCcw className="h-4 w-4" />
                  {order.status === "PartiallyRefunded"
                    ? t("refundRemaining")
                    : t("refund")}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {canRefund && (
          <RefundDialog
            open={refundOpen}
            onOpenChange={setRefundOpen}
            order={order}
          />
        )}
      </>
    );
  }

  const terminalLabel =
    order.status === "Refunded"
      ? t("terminal.Refunded")
      : order.status === "Cancelled"
      ? t("terminal.Cancelled")
      : t("terminal.Expired");

  return (
    <Card>
      <CardContent className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4" />
        {terminalLabel}
      </CardContent>
    </Card>
  );
}

/**
 * İade hareketleri timeline kartı — order detail sol kolonun altında.
 * Her iade ayrı satır: tarih, tutar, neden, kim yaptı, kaç bilet, mail/SMS durumu.
 * Audit log (P3) bu kartla bağlanır; aynı bilgi orada filterable list olarak da verilir.
 */
function RefundTimelineCard({ order }: { order: OrderDetailResponse }) {
  const t = useT("orderDetail.refunds");
  const fmt = useFormat();
  const totalRefunded = order.refundedAmount;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            {t("title", { count: order.refunds.length })}
          </span>
          <span className="tabular-nums text-sm text-muted-foreground">
            {t("total", { value: fmt.money(totalRefunded, order.currency) })}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {order.refunds.map((r) => (
          <RefundTimelineRow key={r.id} refund={r} currency={order.currency} />
        ))}
      </CardContent>
    </Card>
  );
}

function RefundTimelineRow({
  refund,
  currency,
}: {
  refund: RefundInfo;
  currency: string;
}) {
  const t = useT("orderDetail.refunds");
  const refundReasonLabel = useRefundReasonLabel();
  const reasonLabel = refundReasonLabel(refund.reason);
  const fmt = useFormat();
  return (
    <div className="rounded-md border bg-muted/20 p-3 text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-medium">{reasonLabel}</span>
        <span className="tabular-nums font-semibold">
          {fmt.money(refund.amount, currency)}
        </span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        {fmt.dateTime(refund.createdAt, DETAIL_DATE)}
        {refund.actorEmail && <> · {refund.actorEmail}</>}
        {refund.isPartial && <> · {t("partial", { count: refund.refundedTicketCount })}</>}
      </div>
      {refund.reasonNote && (
        <p className="mt-2 text-xs italic text-muted-foreground">
          "{refund.reasonNote}"
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
        {refund.providerRefundId && (
          <Badge variant="outline" className="font-mono">
            {refund.providerRefundId}
          </Badge>
        )}
        <Badge variant={refund.emailSent ? "success" : "secondary"}>
          {refund.emailSent ? t("emailSent") : t("emailNotSent")}
        </Badge>
        <Badge variant={refund.smsSent ? "success" : "secondary"}>
          {refund.smsSent ? t("smsSent") : t("smsNotSent")}
        </Badge>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={
          bold ? "tabular-nums text-base font-semibold" : "tabular-nums"
        }
      >
        {value}
      </span>
    </div>
  );
}

function getErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.detail || fallback;
  if (e instanceof Error) return e.message || fallback;
  return fallback;
}
